import { setScreen, card, title, subtitle, btn, el, input, colors, errorMsg, divider, row, group } from "./app";
import { buildAllegianceToggle, buildRosterForm } from "./rosterForm";
import { showLobby } from "./lobby";
import { joinGame } from "../api/games";
import { decodeShareCode } from "./waitingRoom";
import { GameSession } from "../client/ws/GameSession";
import type { GameClient } from "../client/controller/GameClient";
import type { GameStateSnapshotMessage, ActionPromptMessage } from "../client/ws/OutboundMessage";
import type { InboundMessage } from "../client/ws/InboundMessage";
import type { LocalGameState } from "../client/model/LocalGameState";
import { startGame } from "../game";

export function showJoinGame(): void {
  const c = card(520);

  c.appendChild(title("Join Game"));
  c.appendChild(subtitle("Paste the game code shared by your opponent."));
  c.appendChild(divider());

  // Game code input
  const codeInput = input("Paste game code here…");
  c.appendChild(group("Game Code", codeInput));

  // Allegiance
  const { container: allegianceEl, getAllegiance } = buildAllegianceToggle();
  c.appendChild(allegianceEl);

  // Roster
  const { container: rosterEl, getPlayers, updateAllegiance } = buildRosterForm("ELF");
  c.appendChild(rosterEl);

  // Wire allegiance → roster roles
  c.querySelectorAll<HTMLButtonElement>("[data-value]").forEach((b) => {
    b.addEventListener("click", () => {
      requestAnimationFrame(() => updateAllegiance(getAllegiance()));
    });
  });

  c.appendChild(divider());

  const err = errorMsg();
  c.appendChild(err);

  const backBtn = btn("← Back", "ghost");
  backBtn.addEventListener("click", showLobby);

  const joinBtn = btn("Join Game", "primary");
  joinBtn.style.marginLeft = "auto";

  c.appendChild(row(backBtn, joinBtn));

  joinBtn.addEventListener("click", async () => {
    err.textContent = "";

    const rawCode = codeInput.value.trim();
    if (!rawCode) {
      err.textContent = "Enter the game code.";
      return;
    }

    const parsed = decodeShareCode(rawCode);
    if (!parsed) {
      err.textContent = "Invalid game code format. Expected the code shared by the host.";
      return;
    }

    const allegiance = getAllegiance();
    const players = getPlayers();
    if (players.length === 0) {
      err.textContent = "Add at least one player.";
      return;
    }

    joinBtn.disabled = true;
    joinBtn.textContent = "Joining…";

    try {
      const response = await joinGame(parsed.gameId, {
        joinCode: parsed.joinCode,
        allegiance,
        players,
      });

      showConnectingScreen(parsed.gameId, response.awayToken);
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : "Failed to join game.";
      joinBtn.disabled = false;
      joinBtn.textContent = "Join Game";
    }
  });

  setScreen(c);
}

function showConnectingScreen(gameId: string, token: string): void {
  const c = card(440);

  c.appendChild(title("Joined!"));

  const msg = el("p", { margin: "0 0 24px 0", color: colors.muted, fontSize: "14px" });
  msg.textContent = "Connected to game. Waiting for the host…";
  c.appendChild(msg);

  const statusRow = el("div", { display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px" });

  if (!document.getElementById("bb-spinner-style")) {
    const style = document.createElement("style");
    style.id = "bb-spinner-style";
    style.textContent = `@keyframes bb-spin { to { transform: rotate(360deg); } }`;
    document.head.appendChild(style);
  }

  const spinner = el("div", {
    width: "16px",
    height: "16px",
    border: `2px solid ${colors.border}`,
    borderTop: `2px solid ${colors.accent}`,
    borderRadius: "50%",
    flexShrink: "0",
    animation: "bb-spin 0.8s linear infinite",
  });

  const statusText = el("span", { fontSize: "14px", color: colors.muted });
  statusText.textContent = "Connecting…";

  statusRow.appendChild(spinner);
  statusRow.appendChild(statusText);
  c.appendChild(statusRow);

  c.appendChild(el("p", {
    fontSize: "12px",
    color: colors.muted,
    marginBottom: "20px",
  })).textContent = `Game ID: ${gameId}`;

  const leaveBtn = btn("← Leave", "ghost");
  leaveBtn.style.fontSize = "13px";
  c.appendChild(leaveBtn);

  setScreen(c);

  let session: GameSession | null = null;

  const client: GameClient = {
    onSnapshot(snap: GameStateSnapshotMessage, _state: LocalGameState) {
      statusText.textContent = `Connected — ${snap.phase}`;
      spinner.style.borderTopColor = colors.success;
    },
    onEventBatch(_events, _state) {},
    onFormationAccepted() {
      statusText.textContent = "Formation accepted. Game starting…";
    },
    onError(error: string) {
      statusText.textContent = `Error: ${error}`;
      spinner.style.display = "none";
    },
    async onActionPrompt(_prompt: ActionPromptMessage, _state: LocalGameState): Promise<InboundMessage | null> {
      return null;
    },
    onGameStarted(_state) {
      startGame(session!, false);
    },
  };

  session = new GameSession(gameId, token, client);
  session.connect();

  leaveBtn.addEventListener("click", () => {
    session.disconnect();
    showLobby();
  });
}
